import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart, TrendingUp, TrendingDown, Users, Loader2, RefreshCw, Search,
  Filter, DollarSign, Coins, ArrowDownToLine, ChevronLeft,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DemoBanner from "@/components/DemoBanner";

/**
 * AdminFinance — platform-wide financial dashboard.
 *
 * Route: /admin/finance
 * Access: any is_admin=True user (staff + super_admin).
 *
 * Shows:
 *   - Total gross revenue (all-time)
 *   - Platform's 30% cut (net revenue)
 *   - Streamers' 70% earned (unpaid = outstanding liability)
 *   - Total already paid out
 *   - Top 10 earners
 *   - Transactions table (all ledger rows, filterable)
 */

const TYPE_META = {
  tip: { label: "Tip · 100%→streamer", color: "pink" },
  channel_sub_gc: { label: "Channel Sub · GC (70/30)", color: "cyan" },
  channel_sub_stripe: { label: "Channel Sub · Stripe (70/30)", color: "cyan" },
  platform_sub_gc: { label: "Platform Sub · GC (100%→platform)", color: "volt" },
  platform_sub_stripe: { label: "Platform Sub · Stripe (100%→platform)", color: "volt" },
  sub_stripe: { label: "Platform Sub (legacy)", color: "volt" },
  coin_pack_sale: { label: "Coin Pack Sale (liability)", color: "orange" },
  credit_pack: { label: "Coin Pack (legacy)", color: "orange" },
  payout: { label: "Payout", color: "orange" },
  reversal: { label: "Reversal", color: "pink" },
};

export default function AdminFinance() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(true);
  const [type, setType] = useState("");
  const [streamer, setStreamer] = useState("");
  const [source, setSource] = useState("");

  useEffect(() => {
    if (user && !user.is_admin && !user.is_demo) { nav("/"); return; }
  }, [user, nav]);

  const loadSummary = () => {
    setLoading(true);
    api.get("/admin/finance/summary")
      .then((r) => setSummary(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load summary"))
      .finally(() => setLoading(false));
  };

  const loadTxs = () => {
    setTxLoading(true);
    const params = { limit: 100 };
    if (type) params.type = type;
    if (streamer.trim()) params.streamer = streamer.trim();
    if (source) params.source = source;
    api.get("/admin/finance/transactions", { params })
      .then((r) => {
        setTxs(r.data.transactions);
        setTxTotal(r.data.total);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load transactions"))
      .finally(() => setTxLoading(false));
  };

  useEffect(() => { loadSummary(); loadTxs(); }, []);
  useEffect(() => { loadTxs(); }, [type, streamer, source]);

  if (!user?.is_admin && !user?.is_demo) return null;

  const formatCents = (c) => `$${((c || 0) / 100).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-[#0A0A0C]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <DemoBanner />
        <button
          type="button"
          onClick={() => nav("/admin")}
          className="text-[10px] sc-mono text-zinc-500 hover:text-zinc-300 mb-4 flex items-center gap-1"
        >
          <ChevronLeft className="w-3 h-3" /> BACK TO ADMIN
        </button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="sc-label" style={{ color: "var(--sc-volt)" }}>ADMIN · FINANCE</div>
            <h1 className="sc-heading text-3xl sm:text-4xl font-black tracking-tight">Platform Ledger</h1>
            <div className="sc-mono text-xs text-zinc-500 mt-1">
              Immutable transaction log · tips 100% streamer · channel subs 70/30 · platform subs 100% platform
            </div>
          </div>
          <button
            type="button"
            onClick={() => { loadSummary(); loadTxs(); }}
            className="sc-btn-ghost text-xs flex items-center gap-1.5"
            data-testid="admin-finance-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {loading || !summary ? (
          <div className="term-card p-10 text-center">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-zinc-500" />
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <SummaryCard
                icon={LineChart}
                color="cyan"
                label="TOTAL GROSS REVENUE"
                value={formatCents(summary.total_gross_cents)}
                hint="Tips + all subs (recognized)"
                testid="admin-finance-gross"
              />
              <SummaryCard
                icon={TrendingUp}
                color="volt"
                label="PLATFORM REVENUE"
                value={formatCents(summary.total_platform_fees_cents)}
                hint="30% of channel subs + 100% of platform subs"
                testid="admin-finance-platform"
              />
              <SummaryCard
                icon={Users}
                color="pink"
                label="STREAMERS EARNED"
                value={formatCents(summary.total_streamer_earned_cents)}
                hint="100% of tips + 70% of channel subs"
                testid="admin-finance-earned"
              />
              <SummaryCard
                icon={ArrowDownToLine}
                color="orange"
                label="OUTSTANDING LIABILITY"
                value={formatCents(summary.outstanding_liability_cents)}
                hint={`${formatCents(summary.total_paid_out_cents)} already paid`}
                testid="admin-finance-liability"
              />
            </div>

            {/* Coin reserve — money we've collected for coins that haven't been "spent" yet. */}
            {summary.coin_reserve && (
              <div className="term-card orange p-4 mb-6" data-testid="admin-finance-reserve">
                <div className="flex items-center gap-2 mb-3">
                  <Coins className="w-4 h-4" style={{ color: "var(--sc-orange)" }} />
                  <span className="sc-label" style={{ color: "var(--sc-orange)" }}>◆ COIN RESERVE (LIABILITY)</span>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <div className="text-[10px] sc-mono text-zinc-500 mb-1">COINS SOLD</div>
                    <div className="sc-heading text-2xl font-black">{formatCents(summary.coin_reserve.coins_sold_cents)}</div>
                    <div className="text-[10px] text-zinc-500 mt-1">Fiat collected for Gold Coins</div>
                  </div>
                  <div>
                    <div className="text-[10px] sc-mono text-zinc-500 mb-1">COINS SPENT</div>
                    <div className="sc-heading text-2xl font-black">{formatCents(summary.coin_reserve.coins_spent_cents)}</div>
                    <div className="text-[10px] text-zinc-500 mt-1">On tips / subs / platform subs</div>
                  </div>
                  <div>
                    <div className="text-[10px] sc-mono text-zinc-500 mb-1">CURRENT RESERVE</div>
                    <div className="sc-heading text-2xl font-black" style={{ color: "var(--sc-orange)" }}>
                      {formatCents(summary.coin_reserve.reserve_balance_cents)}
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-1">Held until users spend</div>
                  </div>
                </div>
              </div>
            )}

            {/* Split by event type */}
            <div className="term-card p-4 mb-6">
              <div className="sc-label mb-3" style={{ color: "var(--sc-cyan)" }}>◈ REVENUE BY EVENT TYPE</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(summary.by_type).filter(([t]) => t !== "payout" && t !== "reversal").map(([t, row]) => (
                  <div key={t} className="p-3 rounded border border-white/10 bg-white/[0.02]">
                    <div className="flex items-center justify-between mb-1.5">
                      <span
                        className="sc-mono text-[10px] px-1.5 py-0.5 rounded font-bold"
                        style={{ background: `var(--sc-${TYPE_META[t]?.color || "cyan"})22`, color: `var(--sc-${TYPE_META[t]?.color || "cyan"})` }}
                      >
                        {TYPE_META[t]?.label || t}
                      </span>
                      <span className="sc-mono text-[10px] text-zinc-500">{row.count} tx</span>
                    </div>
                    <div className="sc-heading text-xl font-black">{formatCents(row.gross)}</div>
                    <div className="text-[10px] text-zinc-500 mt-1">
                      Streamers: {formatCents(row.streamer_share)} · Platform: {formatCents(row.platform_fee)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top earners */}
            <div className="term-card p-4 mb-6">
              <div className="sc-label mb-3" style={{ color: "var(--sc-pink)" }}>▲ TOP 10 EARNERS</div>
              {summary.top_earners.length === 0 ? (
                <div className="text-xs text-zinc-500 text-center py-6">No earnings yet.</div>
              ) : (
                <div className="space-y-1.5">
                  {summary.top_earners.map((e, i) => (
                    <div key={e.username} className="flex items-center justify-between p-2 rounded hover:bg-white/5">
                      <div className="flex items-center gap-3">
                        <span className="sc-mono text-xs w-6 text-zinc-500 text-right">{i + 1}.</span>
                        <span className="sc-heading text-sm font-bold">{e.username}</span>
                      </div>
                      <span className="sc-mono text-sm font-bold" style={{ color: "var(--sc-volt)" }}>
                        {formatCents(e.earned_cents)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Transactions */}
        <div className="term-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="sc-label" style={{ color: "var(--sc-cyan)" }}>◈ ALL TRANSACTIONS</div>
            <span className="sc-mono text-[10px] text-zinc-500">{txTotal.toLocaleString()} total</span>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-[1fr_auto_auto] gap-2 mb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <input
                type="search"
                value={streamer}
                onChange={(e) => setStreamer(e.target.value)}
                placeholder="Filter by streamer username…"
                data-testid="admin-finance-streamer-filter"
                className="w-full pl-9 pr-3 py-2 bg-[#0F0F14] border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none rounded sc-mono text-sm text-zinc-100 placeholder:text-zinc-600"
              />
            </div>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              data-testid="admin-finance-type-filter"
              className="px-3 py-2 bg-[#0F0F14] border border-white/10 rounded sc-mono text-xs text-zinc-100"
            >
              <option value="">All types</option>
              {Object.entries(TYPE_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              data-testid="admin-finance-source-filter"
              className="px-3 py-2 bg-[#0F0F14] border border-white/10 rounded sc-mono text-xs text-zinc-100"
            >
              <option value="">All sources</option>
              <option value="gold_coins">Gold Coins</option>
              <option value="stripe">Stripe</option>
              <option value="stripe_connect">Stripe Connect</option>
              <option value="mock">Mock</option>
            </select>
          </div>

          {/* Tx table */}
          {txLoading ? (
            <div className="text-center py-10"><Loader2 className="w-5 h-5 mx-auto animate-spin text-zinc-500" /></div>
          ) : txs.length === 0 ? (
            <div className="text-center py-10 text-xs text-zinc-500">No transactions matching filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="admin-finance-tx-table">
                <thead>
                  <tr className="border-b border-white/10 text-zinc-500 sc-mono text-[10px]">
                    <th className="text-left px-2 py-2">DATE</th>
                    <th className="text-left px-2 py-2">TYPE</th>
                    <th className="text-left px-2 py-2">STREAMER</th>
                    <th className="text-left px-2 py-2">FROM</th>
                    <th className="text-right px-2 py-2">GROSS</th>
                    <th className="text-right px-2 py-2">SHARE</th>
                    <th className="text-right px-2 py-2">FEE</th>
                    <th className="text-left px-2 py-2">SOURCE</th>
                    <th className="text-center px-2 py-2">PAID</th>
                  </tr>
                </thead>
                <tbody>
                  {txs.map((tx) => {
                    const meta = TYPE_META[tx.type] || { label: tx.type, color: "cyan" };
                    const isPayout = tx.type === "payout";
                    return (
                      <tr key={tx.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`admin-finance-tx-${tx.id}`}>
                        <td className="px-2 py-1.5 sc-mono text-zinc-500 whitespace-nowrap">
                          {new Date(tx.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className="px-2 py-1.5">
                          <span
                            className="sc-mono text-[9px] px-1.5 py-0.5 rounded font-bold"
                            style={{ background: `var(--sc-${meta.color})22`, color: `var(--sc-${meta.color})` }}
                          >
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-zinc-300 truncate max-w-[120px]">{tx.streamer_username || "—"}</td>
                        <td className="px-2 py-1.5 text-zinc-400 truncate max-w-[120px]">{tx.from_username || (isPayout ? "→ bank" : "—")}</td>
                        <td className="px-2 py-1.5 text-right sc-mono text-zinc-300">{formatCents(tx.gross_usd_cents)}</td>
                        <td className="px-2 py-1.5 text-right sc-mono font-bold" style={{ color: isPayout ? "var(--sc-orange)" : "var(--sc-volt)" }}>
                          {isPayout ? "-" : ""}{formatCents(tx.streamer_share_cents)}
                        </td>
                        <td className="px-2 py-1.5 text-right sc-mono text-zinc-500">{formatCents(tx.platform_fee_cents)}</td>
                        <td className="px-2 py-1.5 sc-mono text-[10px] text-zinc-500">{tx.source}</td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`text-[9px] sc-mono ${tx.paid_out ? "text-zinc-500" : "text-[color:var(--sc-volt)]"}`}>
                            {tx.paid_out ? "PAID" : "OPEN"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function SummaryCard({ icon: Icon, color, label, value, hint, testid }) {
  return (
    <div className={`term-card ${color} p-4`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5" style={{ color: `var(--sc-${color})` }} />
        <span className="sc-label text-[10px]" style={{ color: `var(--sc-${color})` }}>{label}</span>
      </div>
      <div className="sc-heading text-2xl sm:text-3xl font-black tracking-tight">{value}</div>
      <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>
    </div>
  );
}
